import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Box, Button, Card, Chip, CircularProgress, Collapse, IconButton, InputAdornment, MenuItem, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';

import {
    getSurvey, listSurveyResponses, exportSurveyResponses, downloadCsv, fmtDateTime,
} from '../../services/surveys.service';

/**
 * A survey's responses: newest first, each expandable to its answers. Answers are shown
 * with the wording of the version they were given in.
 *
 * Filters (all applied on the server, and to the CSV export too):
 *   version, from/to dates, programme,
 *   answer filters — any choice question = an option, or a details-form field (gender,
 *   country…) = an option; several filters must all match,
 *   search — free text and the details form's text fields (name, school, role…).
 *
 * Export: every matching response as CSV, one row each — with option codes (stable
 * across versions, best for analysis) or with labels (easier to read).
 *
 * Material-UI v4, so styling is `style=` rather than `sx=`.
 */

const fmtDuration = (ms) => {
    if (!ms) return '—';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const AnswerList = ({ answers }) => (
    <Table size="small">
        <TableBody>
            {answers.map((a, i) => (
                <TableRow key={`${a.q}-${a.row || i}`}>
                    <TableCell style={{ width: 70, fontFamily: 'monospace', color: '#888', verticalAlign: 'top' }}>{a.q}</TableCell>
                    <TableCell style={{ width: '38%', verticalAlign: 'top', color: '#555' }}>
                        {a.question}
                        {a.row && <div style={{ fontWeight: 600, color: '#333' }}>{a.row_label}</div>}
                    </TableCell>
                    <TableCell style={{ verticalAlign: 'top' }}>
                        {a.fields ? (
                            a.fields.map((fd) => (
                                <div key={fd.key}><span style={{ color: '#888' }}>{fd.label}:</span> {fd.value}</div>
                            ))
                        ) : a.row ? (
                            <>
                                {a.statement && <div>{a.statement}</div>}
                                {a.rating && <div style={{ color: '#666' }}>Rating: {a.rating}</div>}
                            </>
                        ) : (
                            <>
                                {a.ranked
                                    ? <ol style={{ margin: 0, paddingLeft: 18 }}>{a.answers.map((x) => <li key={x}>{x}</li>)}</ol>
                                    : a.answers.map((x) => <div key={x}>{x}</div>)}
                                {a.other_text && <div style={{ color: '#666', marginTop: 4 }}>Other: “{a.other_text}”</div>}
                                {a.extra_text && <div style={{ color: '#666', marginTop: 4 }}>Comment: “{a.extra_text}”</div>}
                            </>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

// "Ms Teacher · City School" from the details form, when the response has one.
const respondentOf = (r) => {
    const form = r.answers.find((a) => a.fields);
    if (!form) return '—';
    const pick = (key) => (form.fields.find((f) => f.key === key) || {}).value;
    return [pick('name'), pick('school')].filter(Boolean).join(' · ') || '—';
};

const ResponseRow = ({ r }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox">
                    <IconButton size="small" aria-label="Show answers">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                </TableCell>
                <TableCell>{fmtDateTime(r.submitted_at)}</TableCell>
                <TableCell>{respondentOf(r)}</TableCell>
                <TableCell>v{r.survey_version}</TableCell>
                <TableCell>{r.context.programme || '—'}</TableCell>
                <TableCell>{fmtDuration(r.context.duration_ms)}</TableCell>
                <TableCell align="right">{new Set(r.answers.map((a) => a.q)).size}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={7} style={{ padding: 0, borderBottom: open ? undefined : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box style={{ padding: '8px 16px 16px', background: '#fafafa' }}>
                            <AnswerList answers={r.answers} />
                            <Typography variant="caption" style={{ color: '#aaa' }}>Response {r.id}</Typography>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

/**
 * What can be filtered on, from the live version: every question with options (single,
 * multi, rank) and every details-form field with options (gender, country…).
 */
const buildFilterChoices = (def) => {
    if (!def) return [];
    const out = [];
    (def.questions || []).forEach((q) => {
        if (q.type !== 'matrix' && q.type !== 'form' && (q.options || []).length) {
            out.push({
                id: q.id,
                label: `${q.id} · ${q.title}`,
                options: q.options.map((o) => ({ value: o.code, label: o.label })),
                toFilter: (code) => ({ q: q.id, code }),
            });
        }
        (q.fields || []).filter((f) => (f.options || []).length).forEach((f) => {
            out.push({
                id: `${q.id}.${f.key}`,
                label: `${q.kicker || q.id} · ${f.label}`,
                options: f.options.map((o) => ({ value: o.code, label: o.label })),
                toFilter: (code) => ({ q: q.id, key: f.key, value: code }),
            });
        });
    });
    return out;
};

const SurveyResponses = () => {
    const { survey_code } = useParams();
    const history = useHistory();

    const [title, setTitle] = useState(survey_code);
    const [liveDef, setLiveDef] = useState(null);
    const [versions, setVersions] = useState([]);
    const [version, setVersion] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [programme, setProgramme] = useState('');
    const [answerFilters, setAnswerFilters] = useState([]); // [{ id, choiceLabel, value, valueLabel }]
    const [pickChoice, setPickChoice] = useState('');
    const [pickValue, setPickValue] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState('');
    const [error, setError] = useState('');

    // Version choices, the title and the live questions (for the filter choices).
    useEffect(() => {
        getSurvey(survey_code)
            .then((res) => {
                const d = res && res.data;
                if (!d) return;
                setTitle((d.live && d.live.definition.title) || survey_code);
                setLiveDef(d.live ? d.live.definition : null);
                setVersions(d.versions.filter((v) => v.status === 'published'));
            })
            .catch(() => { /* the list below shows the error */ });
    }, [survey_code]);

    const filterChoices = useMemo(() => buildFilterChoices(liveDef), [liveDef]);
    const programmeChoices = useMemo(() => {
        const p0 = liveDef && (liveDef.questions || []).find((q) => q.id === 'P0');
        return p0 ? p0.options.map((o) => ({ value: o.code.replace(/^P_/, ''), label: o.label })) : [];
    }, [liveDef]);

    // Typing in the search box waits for a pause before querying.
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    // An out-of-order response must not overwrite a newer one.
    const reqId = useRef(0);
    const apiFilters = useMemo(() => answerFilters.map((f) => {
        const choice = filterChoices.find((c) => c.id === f.id);
        return choice ? choice.toFilter(f.value) : null;
    }).filter(Boolean), [answerFilters, filterChoices]);

    const params = {
        survey_code,
        version: version || undefined,
        from: from || undefined,
        to: to || undefined,
        programme: programme || undefined,
        filters: apiFilters.length ? JSON.stringify(apiFilters) : undefined,
        search: search || undefined,
    };
    const filtering = Boolean(version || from || to || programme || apiFilters.length || search);

    const load = useCallback(async () => {
        const mine = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await listSurveyResponses({ ...params, page: page + 1, limit: rowsPerPage });
            if (mine !== reqId.current) return;
            setRows((res && res.data && res.data.responses) || []);
            setTotal((res && res.data && res.data.total) || 0);
        } catch (e) {
            if (mine !== reqId.current) return;
            setRows([]);
            setTotal(0);
            setError((e && e.message) || 'Could not load responses.');
        } finally {
            if (mine === reqId.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [survey_code, version, from, to, programme, apiFilters, search, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const onFilter = (setter) => (e) => { setter(e.target.value); setPage(0); };
    const onRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    const pickedChoice = filterChoices.find((c) => c.id === pickChoice);
    const addAnswerFilter = () => {
        if (!pickedChoice || !pickValue) return;
        const opt = pickedChoice.options.find((o) => o.value === pickValue);
        setAnswerFilters((list) => [
            ...list.filter((f) => !(f.id === pickedChoice.id && f.value === pickValue)),
            { id: pickedChoice.id, choiceLabel: pickedChoice.label, value: pickValue, valueLabel: opt ? opt.label : pickValue },
        ]);
        setPickChoice('');
        setPickValue('');
        setPage(0);
    };
    const removeAnswerFilter = (i) => { setAnswerFilters((list) => list.filter((_, j) => j !== i)); setPage(0); };
    const clearAll = () => {
        setVersion(''); setFrom(''); setTo(''); setProgramme(''); setAnswerFilters([]); setSearchInput(''); setSearch(''); setPage(0);
    };

    const exportCsv = async (labels) => {
        setExporting(labels ? 'labels' : 'codes');
        setError('');
        try {
            const res = await exportSurveyResponses({ ...params, labels: labels ? 'true' : undefined });
            downloadCsv(res.data.filename, res.data.csv);
        } catch (e) {
            setError((e && e.message) || 'Could not export.');
        } finally {
            setExporting('');
        }
    };

    return (
        <Box style={{ padding: 24 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/surveys/list')} style={{ marginBottom: 8 }}>
                All surveys
            </Button>
            <Typography variant="h5" style={{ fontWeight: 700 }}>Responses</Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                {title} · each answer is shown with the wording of the version it was given in. Responses with an
                &ldquo;About you&rdquo; section include the respondent&rsquo;s name, school and other personal details,
                often of minors in the student survey: personal data, handle exports accordingly.
            </Typography>

            <Card style={{ padding: 16 }}>
                {/* Row 1: version, dates, programme, search */}
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                    <TextField select label="Version" value={version} onChange={onFilter(setVersion)} variant="outlined" size="small" style={{ width: 150 }}>
                        <MenuItem value="">All versions</MenuItem>
                        {versions.map((v) => <MenuItem key={v.version} value={String(v.version)}>v{v.version} ({v.responses})</MenuItem>)}
                    </TextField>
                    <TextField label="From" type="date" value={from} onChange={onFilter(setFrom)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="To" type="date" value={to} onChange={onFilter(setTo)} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                    {programmeChoices.length > 0 && (
                        <TextField select label="Programme" value={programme} onChange={onFilter(setProgramme)} variant="outlined" size="small" style={{ width: 150 }}>
                            <MenuItem value="">All programmes</MenuItem>
                            {programmeChoices.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                        </TextField>
                    )}
                    <TextField
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search name, school, role or comments"
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 100 }}
                        style={{ flex: '1 1 260px', maxWidth: 380 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                </Box>

                {/* Row 2: answer filters */}
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                    <TextField
                        select
                        label="Filter by answer"
                        value={pickChoice}
                        onChange={(e) => { setPickChoice(e.target.value); setPickValue(''); }}
                        variant="outlined"
                        size="small"
                        style={{ width: 320 }}
                    >
                        <MenuItem value="">Choose a question</MenuItem>
                        {filterChoices.map((c) => (
                            <MenuItem key={c.id} value={c.id} style={{ whiteSpace: 'normal', maxWidth: 560 }}>{c.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Answer"
                        value={pickValue}
                        onChange={(e) => setPickValue(e.target.value)}
                        variant="outlined"
                        size="small"
                        disabled={!pickedChoice}
                        style={{ width: 280 }}
                    >
                        {(pickedChoice ? pickedChoice.options : []).map((o) => (
                            <MenuItem key={o.value} value={o.value} style={{ whiteSpace: 'normal', maxWidth: 520 }}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <Button variant="outlined" color="primary" startIcon={<AddIcon />} disabled={!pickedChoice || !pickValue} onClick={addAnswerFilter}>
                        Add filter
                    </Button>
                    {filtering && <Button onClick={clearAll}>Clear all</Button>}
                    <Box style={{ flex: 1 }} />
                    <Button variant="outlined" startIcon={<GetAppIcon />} disabled={!!exporting || !total} onClick={() => exportCsv(false)}>
                        {exporting === 'codes' ? 'Exporting…' : 'CSV (codes)'}
                    </Button>
                    <Button variant="outlined" startIcon={<GetAppIcon />} disabled={!!exporting || !total} onClick={() => exportCsv(true)}>
                        {exporting === 'labels' ? 'Exporting…' : 'CSV (labels)'}
                    </Button>
                </Box>

                {answerFilters.length > 0 && (
                    <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {answerFilters.map((f, i) => (
                            <Chip
                                key={`${f.id}-${f.value}`}
                                size="small"
                                label={`${f.choiceLabel.length > 60 ? `${f.choiceLabel.slice(0, 57)}…` : f.choiceLabel} = ${f.valueLabel}`}
                                onDelete={() => removeAnswerFilter(i)}
                            />
                        ))}
                    </Box>
                )}

                <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>
                    {loading ? 'Loading…' : `${total} response${total === 1 ? '' : 's'}${filtering ? ' match' : ''}`}
                </Typography>

                {error && <Typography color="error" style={{ marginBottom: 12 }}>{error}</Typography>}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Submitted</TableCell>
                                <TableCell>Respondent</TableCell>
                                <TableCell>Version</TableCell>
                                <TableCell>Programme</TableCell>
                                <TableCell>Time taken</TableCell>
                                <TableCell align="right">Questions answered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={7} style={{ textAlign: 'center', padding: 32 }}><CircularProgress size={24} /></TableCell></TableRow>
                            )}
                            {!loading && rows.length === 0 && (
                                <TableRow><TableCell colSpan={7} style={{ color: '#888' }}>
                                    {filtering ? 'No responses match these filters.' : 'No responses yet.'}
                                </TableCell></TableRow>
                            )}
                            {!loading && rows.map((r) => <ResponseRow key={r.id} r={r} />)}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    // v4 names (onChangePage) and the newer ones, as on the CPC requests page.
                    onChangePage={(e, p) => setPage(p)}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={onRowsPerPage}
                    onRowsPerPageChange={onRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </Card>
        </Box>
    );
};

export default SurveyResponses;
